import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogIn, LayoutDashboard, Menu, X } from "lucide-react";
import KairosisLogo from "@/components/KairosisLogo";
import { isPaidPlan, normalizePlan } from "@/lib/plans";

export default function Navbar({ onSignIn }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const plan = normalizePlan(user.subscription_plan);
  const status = user.subscription_status || 'inactive';
  const isPaid = token && isPaidPlan(plan, status);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Forex Calendar', path: '/live' },
    { name: 'Performance', path: '/performance' },
    { name: 'Leaderboard', path: '/leaderboard' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'How It Works', path: '/how-it-works' },
    { name: 'FAQ', path: '/faq' },
  ];

  const handleSignIn = () => {
    if (onSignIn) {
      onSignIn();
    } else {
      navigate('/pricing', { state: { openSignIn: true } });
    }
    setMobileMenuOpen(false);
  };

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled || mobileMenuOpen ? 'glassmorphism' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex justify-between items-center h-16">
          <a href="/" className="hover:opacity-80 transition-opacity">
            <KairosisLogo size="sm" className="md:[&_span]:text-lg" />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-[#00E676] ${location.pathname === link.path ? 'text-[#00E676]' : 'text-[#A1A1AA]'}`}
              >
                {link.name}
              </Link>
            ))}

            {isPaid ? (
              <Button
                data-testid="nav-dashboard-btn"
                onClick={() => navigate('/dashboard')}
                className="bg-[#00E676] hover:bg-[#00C853] text-black rounded-md"
              >
                Dashboard
              </Button>
            ) : token ? (
              <Button
                data-testid="nav-signout-btn"
                onClick={() => {
                  localStorage.removeItem('token');
                  localStorage.removeItem('user');
                  navigate('/');
                }}
                className="bg-[#1C1C1E] hover:bg-[#27272A] text-white border border-[#27272A] rounded-md"
              >
                Sign Out
              </Button>
            ) : (
              <Button
                data-testid="nav-get-access-btn"
                onClick={handleSignIn}
                className="bg-[#00E676] hover:bg-[#00C853] text-black rounded-md btn-glow"
              >
                Get Access
              </Button>
            )}
          </div>

          {/* Mobile — hamburger + action button */}
          <div className="md:hidden flex items-center gap-2">
            {isPaid && (
              <Button
                data-testid="mobile-dashboard-btn"
                onClick={() => navigate('/dashboard')}
                size="sm"
                className="bg-[#00E676] hover:bg-[#00C853] text-black rounded-full px-3 h-8 text-xs font-semibold gap-1"
              >
                <LayoutDashboard className="w-3.5 h-3.5" />
                <span className="hidden xs:inline">Dashboard</span>
              </Button>
            )}
            {!isPaid && !token && (
              <Button
                data-testid="mobile-signin-btn"
                onClick={handleSignIn}
                size="sm"
                className="bg-[#00E676] hover:bg-[#00C853] text-black rounded-full px-3 h-8 text-xs font-semibold gap-1"
              >
                <LogIn className="w-3.5 h-3.5" />
                Sign In
              </Button>
            )}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-[#A1A1AA] hover:text-white hover:bg-[#1C1C1E] transition-colors"
              aria-label="Toggle navigation menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-[#27272A] bg-[#0A0A0A]/95 backdrop-blur-xl">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'text-[#00E676] bg-[#00E676]/10'
                    : 'text-[#A1A1AA] hover:text-white hover:bg-[#1C1C1E]'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <div className="pt-3 mt-3 border-t border-[#27272A]">
              {isPaid ? (
                <Button
                  onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                  className="w-full bg-[#00E676] hover:bg-[#00C853] text-black h-10 text-sm font-semibold"
                >
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Go to Dashboard
                </Button>
              ) : token ? (
                <Button
                  data-testid="mobile-signout-btn"
                  onClick={() => {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    navigate('/');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full bg-[#1C1C1E] hover:bg-[#27272A] text-white border border-[#27272A] h-10 text-sm font-semibold"
                >
                  Sign Out
                </Button>
              ) : (
                <Button
                  data-testid="mobile-menu-signin-btn"
                  onClick={handleSignIn}
                  className="w-full bg-[#00E676] hover:bg-[#00C853] text-black h-10 text-sm font-semibold"
                >
                  <LogIn className="w-4 h-4 mr-2" /> Sign In
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
